import React, { useState, useRef, useLayoutEffect, useEffect } from "react";
import { createPortal } from "react-dom";
import cn from "classnames";
import OutsideClickHandler from "react-outside-click-handler";
import styles from "./Actions.module.sass";
import Icon from "../Icon";

const OPTION_HEIGHT_ESTIMATE = 40;

function getScrollParent(el) {
  let parent = el?.parentElement;
  while (parent) {
    const style = getComputedStyle(parent);
    const overflowY = style.overflowY || style.overflow;
    if (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
}

const Actions = ({
  className,
  classActionsHead,
  classActionsBody,
  classActionsOption,
  items,
  visible,
  setVisible,
  small,
  up: upProp,
  portal,
}) => {
  const [innerVisible, setInnerVisible] = useState(false);
  const headRef = useRef(null);
  const bodyRef = useRef(null);
  const [portalState, setPortalState] = useState({
    top: 0,
    right: 0,
    up: false,
  });

  const isVisible = visible != null ? visible : innerVisible;

  const toggle = () => {
    setVisible ? setVisible(!visible) : setInnerVisible(!innerVisible);
  };

  const close = () => {
    setVisible ? setVisible(false) : setInnerVisible(false);
  };

  useLayoutEffect(() => {
    if (portal && isVisible && headRef.current) {
      const rect = headRef.current.getBoundingClientRect();
      const estimatedHeight = items.length * OPTION_HEIGHT_ESTIMATE + 24;
      const spaceBelow = window.innerHeight - rect.bottom - 12;
      const shouldOpenUp = upProp ?? spaceBelow < estimatedHeight;

      const rightOffset = small ? 8 : 12;
      setPortalState({
        top: shouldOpenUp ? rect.top - estimatedHeight - 12 : rect.bottom + 12,
        right: window.innerWidth - rect.right - rightOffset,
        up: shouldOpenUp,
      });
    }
  }, [portal, isVisible, items.length, small, upProp]);

  useEffect(() => {
    if (!portal || !isVisible) return;
    const handleClickOutside = (e) => {
      if (
        headRef.current?.contains(e.target) ||
        bodyRef.current?.contains(e.target)
      ) {
        return;
      }
      close();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [portal, isVisible]);

  useEffect(() => {
    if (!isVisible || !headRef.current) return;
    const scrollParent = getScrollParent(headRef.current);
    const target = scrollParent || window;
    const handleScroll = () => close();
    target.addEventListener("scroll", handleScroll, { passive: true });
    return () => target.removeEventListener("scroll", handleScroll);
  }, [isVisible]);

  const bodyContent = (
    <div
      ref={portal ? bodyRef : undefined}
      className={cn(
        styles.body,
        classActionsBody,
        { [styles.portalBody]: portal && isVisible },
        { [styles.up]: upProp ?? (portal && portalState.up) }
      )}
      style={
        portal && isVisible
          ? {
              position: "fixed",
              top: portalState.top,
              right: portalState.right,
              left: "auto",
              visibility: "visible",
              opacity: 1,
              transform: "none",
              zIndex: 9999,
            }
          : undefined
      }
    >
      {items.map((x, index) => (
        <button
          type="button"
          className={cn(styles.option, classActionsOption, x.danger && styles.optionDanger, x.className)}
          onClick={x.action}
          key={index}
        >
          {x.icon && <Icon name={x.icon} size="24" />}
          {x.title}
        </button>
      ))}
    </div>
  );

  const content = (
    <div
      className={cn(
        styles.actions,
        className,
        { [styles.small]: small },
        { [styles.up]: upProp ?? (portal && portalState.up) },
        { [styles.active]: isVisible },
        { [styles.portalActive]: portal && isVisible }
      )}
    >
      <button
        ref={headRef}
        className={cn(styles.head, classActionsHead)}
        onClick={() => toggle()}
      >
        <Icon name="more-horizontal" size="24" />
      </button>
      {portal && isVisible
        ? createPortal(bodyContent, document.body)
        : bodyContent}
    </div>
  );

  if (portal) {
    return content;
  }

  return (
    <OutsideClickHandler onOutsideClick={close}>{content}</OutsideClickHandler>
  );
};

export default Actions;
